import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/api/queryKeys';
import { formsApi } from '../api/formsApi';
import type { FormTriggerParserConfirmInput, FormTriggerRuleWrite } from '../api/types';

export function useFormTriggerRules(formId: string | null | undefined) {
  return useQuery({
    queryKey: keys.forms.triggerRules(formId ?? ''),
    queryFn: ({ signal }) => formsApi.getTriggerRules(formId!, { signal }),
    enabled: formId != null && formId !== '',
    staleTime: 60_000,
  });
}

export function useCreateTriggerRuleMutation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormTriggerRuleWrite) => formsApi.createTriggerRule(formId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.triggerRules(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(formId) });
    },
  });
}

export function useUpdateTriggerRuleMutation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: Partial<FormTriggerRuleWrite> }) =>
      formsApi.patchTriggerRule(formId, ruleId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.triggerRules(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(formId) });
    },
  });
}

export function useDeleteTriggerRuleMutation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => formsApi.deleteTriggerRule(formId, ruleId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.triggerRules(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(formId) });
    },
  });
}

export function useFormTriggerParser(formId: string | null | undefined) {
  return useQuery({
    queryKey: keys.forms.triggerParser(formId ?? ''),
    queryFn: ({ signal }) => formsApi.getTriggerParser(formId!, { signal }),
    enabled: formId != null && formId !== '',
    staleTime: 60_000,
  });
}

export function useUpdateTriggerParserMutation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { raw_text?: string; parsed?: Record<string, unknown> }) =>
      formsApi.putTriggerParser(formId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.triggerParser(formId) });
    },
  });
}

export function useParseTriggerParserMutation(formId: string) {
  return useMutation({
    mutationFn: (rawPrompt: string) => formsApi.parseTriggerParser(formId, { raw_prompt: rawPrompt }),
  });
}

export function useConfirmTriggerParserMutation(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormTriggerParserConfirmInput) => formsApi.confirmTriggerParser(formId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.forms.triggerParser(formId) });
      void queryClient.invalidateQueries({ queryKey: keys.forms.triggerRules(formId) });
    },
  });
}
